import {
  useCallback,
  useEffect,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ROUTE_PATH } from "utils/constants";
import { QUERY_KEYS } from "utils/constants/queryKeys";
import { perPageOptions } from "utils/constants/options";
import { adminService } from "apiServices/apiController/adminService";
import { parseQueryString } from "utils/functions/parseQueryString";
import { toast } from "react-toastify";

import HeaderWithActions from "components/Displays/HeaderWithActions";
import { BGLoading, Buttons, Displays, Icons, Loading, Modal, PaginationNormal, TableCustom } from "components";
import { LeaderboardContainerStyled } from "./styled";
import DepartmentMemberModal from "./DepartmentMemberModal";
import ExpandedRankContent from "./ExpandedRankContent";


const LeaderboardContainer = () => {
  const { id } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [isBgLoading, setIsBgLoading] = useState(false);
  const [dataList, setDataList] = useState({
    mission: null,
    data: [],
  });
  const [pagination, setPagination] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [memberModalData, setMemberModalData] = useState(null);
  const [memberTab, setMemberTab] = useState("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    const stringQuery = parseQueryString({ per_page: perPage, offset: (currentPage - 1) * perPage });
    const res = await adminService.GET_MISSION_V2_LEADERBOARD_OVERVIEW(id, stringQuery);
    if (res?.success) {
      setDataList({
        mission: res.data?.mission,
        data: res.data?.data || [],
      });
      setPagination(res.page ? { ...res.page } : null);
    } else {
      toast.error(
        <Displays.MsgToast
          theme_error_toast
          labelTop="โหลดข้อมูลไม่สำเร็จ"
          labelBottom={'มีข้อผิดพลาดบางอย่าง กรุณาลองใหม่อีกครั้ง'}
        />
      );
    }
    setIsLoading(false);
  }, [id, currentPage, perPage]);

  const isDepartmentMode = dataList?.mission?.challenge_type === "department";

  const handleBack = useCallback(() => {
    history.push(ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2_DETAIL.replace(":id", id));
  }, [history, id]);

  const handleToggleExpand = useCallback((rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  }, []);

  const handleOpenMemberModal = useCallback((department) => {
    setMemberTab("active");
    setMemberModalData(department);
  }, []);

  const handleCloseMemberModal = useCallback(() => {
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.DEPARTMENT_MEMBERS, id],
    });
    setMemberModalData(null);
  }, [queryClient, id]);

  const handleDownload = useCallback(async () => {
    setIsBgLoading(true);
    const res = await adminService.EXPORT_MISSION_V2_REPORT(id);
    if (res?.success && res?.data?.uri) {
      const link = document.createElement("a");
      link.href = res.data.uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error(
        <Displays.MsgToast
          theme_error_toast
          labelTop="ดาวน์โหลดข้อมูลไม่สำเร็จ"
          labelBottom="มีข้อผิดพลาดบางอย่าง กรุณาลองใหม่อีกครั้ง"
        />
      );
    }
    setIsBgLoading(false);
  }, [id]);


  const renderExpandedContent = useCallback(
    (row) => (
      <ExpandedRankContent
        isDepartmentMode={isDepartmentMode}
        row={row}
        onOpenMemberModal={handleOpenMemberModal}
      />
    ),
    [isDepartmentMode, handleOpenMemberModal]
  );


  const columns = ({ nameTitle = "ชื่อบริษัท" } = {}) => [
    {
      key: "rank",
      title: "อันดับ",
      gridWidth: "8%",
      field: "rank",
      render: ({ value }) => <Displays.RankDisplay rank={value} />,
    },
    {
      key: "name_th",
      title: nameTitle,
      gridWidth: "25%",
      field: "name_th",
      className: "name_cell",
    },
    {
      key: "total_achieved_amount",
      title: "ผลการทำภารกิจ",
      gridWidth: "30%",
      field: "total_achieved_amount",
      render: ({ value, origin }) => (
        <Displays.MetricCell label={`จำนวน${origin?.unit_category}สะสม`} value={value} unit={origin?.unit_category || "ก้าว"} />
      ),
    },
    {
      key: "total_participants",
      title: "จำนวนผู้เข้าร่วมภารกิจ",
      gridWidth: "22%",
      field: "total_participants",
      render: ({ value }) => (
        <Displays.MetricCell label="จำนวน" value={value} unit="คน" />
      ),
    },
  ];

  const tableColumns = columns({
    nameTitle: isDepartmentMode ? "ชื่อบริษัท" : "ชื่อทีม",
  });

  useEffect(() => {
    setExpandedRowId(null);
  }, [currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  return (
    <LeaderboardContainerStyled>
      <div className="content_section">
        <BGLoading visible={isBgLoading} />
        {isLoading ? (
          <div className="loading_wrap">
            <Loading theme_standard_loading />
          </div>
        ) : (
          <>
            <div className="header_area">
              <HeaderWithActions
                theme_admin
                handleBackCustom={handleBack}
                title="ตารางอันดับ Leaderboard"
                subTitle="Leaderboard Overview"
                typeObj={
                  <div style={{ width: '140px' }}>
                    <Buttons.BtnNormal
                      theme_only_border_blue
                      label="ดาวน์โหลด"
                      paddingButton="8px 20px"
                      borderRadius="8px"
                      svg_front={<Icons.Download4 />}
                      onClick={handleDownload}
                    />
                  </div>}
              />
            </div>

            <div className="section_label">
              <span className="label_th">อันดับทั้งหมด</span>
              <span className="label_en">Total view</span>
            </div>

            <TableCustom.TableLeaderboard
              columns={tableColumns}
              data={dataList.data}
              expandedRowId={expandedRowId}
              onToggleExpand={handleToggleExpand}
              expandLabel={isDepartmentMode ? "ดูอันดับแผนก" : "ดูอันดับ"}
              renderExpandedContent={renderExpandedContent}
            />
          </>
        )}
      </div>

      <div className="pagination_section">
        <PaginationNormal
          currentPage={pagination?.current_page || 1}
          fromOrder={pagination?.from || 0}
          toOrder={pagination?.to || 0}
          perPage={pagination?.per_page || 20}
          totalPage={pagination?.total || 0}
          lastPage={pagination?.last_page || 1}
          onChangePerPage={(val) => {
            setPerPage(val);
            setCurrentPage(1);
          }}
          onClickPage={(page) => setCurrentPage(page)}
          widthbox="72px"
          perPageOptions={perPageOptions}
          alwaysShowArrows
        />
      </div>

      <Modal
        theme_modal_standard
        isShow={!!memberModalData}
        handleClickCloseModal={handleCloseMemberModal}
      >
        {memberModalData ? (
          <DepartmentMemberModal
            missionId={id}
            department={memberModalData}
            tab={memberTab}
            onTabChange={setMemberTab}
            onClose={handleCloseMemberModal}
          />
        ) : null}
      </Modal>
    </LeaderboardContainerStyled>
  );
};

export default LeaderboardContainer;
